import { Box, Button, Center, Container, Grid, GridItem, Image, SimpleGrid, Text, Tooltip } from "@chakra-ui/react"
import { MotionHStack } from "component/Motion"
import { PerpLink } from "component/PerpLink"
import { TitleMarketDetail } from "component/Title/TitleMarketDetail"
import { TitleBarStats } from "component/TitleBarStats"
import { RouteName } from "constant/route"
import { useBaseAssetSymbolFromRouterParams, useTickerSymbolFromRouterParams } from "hook/useRouterParams"
import { SmartBackButton } from "layout/MobileHeader/SmartBackButton"
import { EventName } from "module/analytics/types"
import { EmergencyShutdownWarning } from "module/market/component/EmergencyShutdownWarning"
import { OutOfSyncWarning } from "module/network/component/OutOfSyncWarning"
import { useLiquidity } from "module/pool/hook/useLiquidity"
import { ConnectWalletRequired } from "module/wallet/component/ConnectWalletRequired"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import { scrollbarStyle } from "theme/scrollbarStyle"
import { Card } from "../../../../component/Card"
import { LiquidityCard } from "./LiquidityCard"
import { LiquidityListHeader } from "./LiquidityListHeader"
import { LPSummary } from "./LPSummary"
import { PoolSummary } from "./PoolSummary"

const faviconUrl = "https://s2.googleusercontent.com/s2/favicons?domain=https://www.seedle.finance/"

export function PoolDetail() {
    const tickerSymbol = useTickerSymbolFromRouterParams()
    const baseSymbol = useBaseAssetSymbolFromRouterParams()
    const { openLiquidities } = useLiquidity(tickerSymbol)
    const { t } = useTranslation()
    const history = useHistory()

    return (
        <Container maxW="1020px" pt={{ base: 2, md: "40px" }} pb={{ base: 128, md: 12 }} px={{ base: 0, sm: 6 }}>
            <SmartBackButton onClickBack={() => history.push(RouteName.POOL_LIST)} />
            <MotionHStack
                layout
                spacing={8}
                maxWidth="100%"
                mt={{ base: 2, md: 0 }}
                pl={{ base: "72px", sm: 12, md: 0 }}
                minH="40px"
            >
                <TitleMarketDetail />
            </MotionHStack>

            <OutOfSyncWarning pt={4} />
            <EmergencyShutdownWarning tickerSymbol={tickerSymbol} />
            <Grid
                gridTemplateColumns={{
                    base: "auto",
                    lg: "380px 1fr",
                }}
                gridTemplateRows={{
                    base: "auto",
                    lg: "1fr",
                }}
                gridTemplateAreas={{
                    base: `'position' 'info' `,
                    lg: `'info position' 'info position'`,
                }}
                pt={{ base: 4, lg: 6 }}
                pb={6}
                gap={6}
                w={"100%"}
                maxH={{ base: "auto", lg: "calc(100vh - 115px)" }}
            >
                <GridItem gridArea="info" overflow={{ base: "visible", md: "hidden" }}>
                    <PoolSummary tickerSymbol={tickerSymbol} liquidityList={openLiquidities} />
                </GridItem>
                <GridItem gridArea="position" overflow={{ base: "visible", md: "hidden" }}>
                    <Card
                        pt={0}
                        position="relative"
                        sx={scrollbarStyle}
                        overflowY="auto"
                        w="100%"
                        h="100%"
                        display={"flex"}
                        flexDirection={"column"}
                    >
                        <LPSummary tickerSymbol={tickerSymbol} liquidityList={openLiquidities} />
                        <LiquidityListHeader
                            addedLiquidityPositionCount={openLiquidities.length}
                            baseSymbol={baseSymbol}
                            tickerSymbol={tickerSymbol}
                        />
                        <SimpleGrid columns={1} py={4} spacing={4} w="100%" h="auto" flexGrow={1}>
                            <ConnectWalletRequired>
                                {openLiquidities.map(liquidity => (
                                    <LiquidityCard key={liquidity.id} liquidity={liquidity} />
                                ))}
                                {openLiquidities.length === 0 && (
                                    <Center flexGrow={1} minH="300px" w="100%" textAlign="center">
                                        <Text color="whiteAlpha.500">{t("pool.no_position_yet")}</Text>
                                    </Center>
                                )}
                            </ConnectWalletRequired>
                            <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center">
                                <PerpLink
                                    href="https://www.seedle.finance/"
                                    trackingProps={{ eventName: EventName.LINK_SEEDLE_CLICK }}
                                >
                                    <Tooltip label="Visit Seedle for advanced stats">
                                        <Button variant="link" leftIcon={<Image src={faviconUrl} />} size="xs">
                                            Advanced Stats on Seedle ↗
                                        </Button>
                                    </Tooltip>
                                </PerpLink>
                                <PerpLink
                                    href="https://perpsim.com/"
                                    trackingProps={{ eventName: EventName.LINK_PERPSIM_CLICK }}
                                >
                                    <Tooltip label="LP backtesting & more with Perpsim">
                                        <Button variant="link" size="xs">
                                            LP backtesting & more with Perpsim ↗
                                        </Button>
                                    </Tooltip>
                                </PerpLink>
                            </Box>
                        </SimpleGrid>
                    </Card>
                </GridItem>
            </Grid>
            <TitleBarStats title={baseSymbol} />
        </Container>
    )
}
